import React from 'react';
import {
  Copy,
  Scissors,
  Delete,
  Lock,
  Unlock,
  Eyes,
  CloseOne,
  AlignTop,
  AlignBottom,
  AlignLeft,
  AlignRight,
  AlignVertically,
  AlignHorizontally,
} from '@icon-park/react';

export interface ContextMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  divider?: boolean;
}

interface ContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, items, onClose }) => {
  React.useEffect(() => {
    const handleClick = () => onClose();
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      onClose();
    };

    // 延迟添加事件监听，避免立即触发
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClick);
      document.addEventListener('contextmenu', handleContextMenu);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [onClose]);

  return (
    <div
      className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999] min-w-[180px]"
      style={{
        left: `${x}px`,
        top: `${y}px`,
      }}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {item.divider ? (
            <div className="h-px bg-gray-200 my-1" />
          ) : (
            <button
              className={`
                w-full px-4 py-2 text-left text-sm flex items-center gap-3
                ${
                  item.disabled
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-primary-50 hover:text-primary-600'
                }
              `}
              onClick={(e) => {
                e.stopPropagation();
                if (!item.disabled) {
                  item.onClick();
                  onClose();
                }
              }}
              disabled={item.disabled}
            >
              {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
              <span className="flex-1">{item.label}</span>
            </button>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default ContextMenu;

// 预定义菜单项工厂函数
export const createElementContextMenu = (params: {
  t: (key: string) => string;
  selectedCount: number;
  isLocked: boolean;
  isVisible: boolean;
  canAlign: boolean;
  onCopy: () => void;
  onCut: () => void;
  onDelete: () => void;
  onToggleLock: () => void;
  onToggleVisibility: () => void;
  onAlignLeft?: () => void;
  onAlignRight?: () => void;
  onAlignTop?: () => void;
  onAlignBottom?: () => void;
  onAlignVertical?: () => void;
  onAlignHorizontal?: () => void;
}): ContextMenuItem[] => {
  const { t } = params;
  const items: ContextMenuItem[] = [
    {
      label: t('contextMenu.copy'),
      icon: <Copy size={16} />,
      onClick: params.onCopy,
    },
    {
      label: t('contextMenu.cut'),
      icon: <Scissors size={16} />,
      onClick: params.onCut,
    },
    {
      label: t('contextMenu.delete'),
      icon: <Delete size={16} />,
      onClick: params.onDelete,
    },
  ];

  // 对齐选项（仅多选时显示）
  if (params.canAlign && params.selectedCount > 1) {
    items.push({ label: '', divider: true } as any);
    
    if (params.onAlignLeft) {
      items.push({
        label: t('contextMenu.alignLeft'),
        icon: <AlignLeft size={16} />,
        onClick: params.onAlignLeft,
      });
    }
    
    if (params.onAlignRight) {
      items.push({
        label: t('contextMenu.alignRight'),
        icon: <AlignRight size={16} />,
        onClick: params.onAlignRight,
      });
    }
    
    if (params.onAlignTop) {
      items.push({
        label: t('contextMenu.alignTop'),
        icon: <AlignTop size={16} />,
        onClick: params.onAlignTop,
      });
    }
    
    if (params.onAlignBottom) {
      items.push({
        label: t('contextMenu.alignBottom'),
        icon: <AlignBottom size={16} />,
        onClick: params.onAlignBottom,
      });
    }
    
    if (params.onAlignVertical) {
      items.push({
        label: t('contextMenu.alignVCenter'),
        icon: <AlignVertically size={16} />,
        onClick: params.onAlignVertical,
      });
    }
    
    if (params.onAlignHorizontal) {
      items.push({
        label: t('contextMenu.alignHCenter'),
        icon: <AlignHorizontally size={16} />,
        onClick: params.onAlignHorizontal,
      });
    }
  }

  // 锁定/解锁
  items.push({ label: '', divider: true } as any);
  items.push({
    label: params.isLocked ? t('contextMenu.unlock') : t('contextMenu.lock'),
    icon: params.isLocked ? <Unlock size={16} /> : <Lock size={16} />,
    onClick: params.onToggleLock,
  });

  // 显示/隐藏
  items.push({
    label: params.isVisible ? t('contextMenu.hide') : t('contextMenu.show'),
    icon: params.isVisible ? <CloseOne size={16} /> : <Eyes size={16} />,
    onClick: params.onToggleVisibility,
  });

  return items;
};

// 空白处右键菜单
export const createCanvasContextMenu = (params: {
  t: (key: string) => string;
  hasClipboard: boolean;
  onPaste: () => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}): ContextMenuItem[] => {
  const { t } = params;
  return [
    {
      label: t('contextMenu.paste'),
      icon: <Copy size={16} />,
      onClick: params.onPaste,
      disabled: !params.hasClipboard,
    },
    { label: '', divider: true } as any,
    {
      label: t('contextMenu.selectAll'),
      icon: <Copy size={16} />,
      onClick: params.onSelectAll,
    },
    {
      label: t('contextMenu.deselectAll'),
      icon: <CloseOne size={16} />,
      onClick: params.onDeselectAll,
    },
  ];
};
